// internal deps
import { isString, isNil, isObject, format } from "@swiggy-private/js-utils";
import { Result, Err, Ok } from "@swiggy-private/js-fp";

// interfaces
import {
    LinkablePayLater,
    LinkPayLaterURIValue,
    Response,
    GetPayLaterResponse,
    PayLaterResponse,
    CreditLimitBusinessError,
} from "@payment-client/interfaces/PublicPayLater";
import { PayLaterCreditLimitData } from "@swiggy-private/payment-ui";
import { HTTP_METHODS } from "@swiggy-private/checkout-core";

// constants
import { LINK_PAYLATER_URI } from "@payment-client/constants/PayLaterEndpoints";

// Success status code constant
const SUCCESS_STATUS_CODE = 0;

interface PayLaterHttpOptionsResponse {
    method: Partial<HTTP_METHODS>;
    endpoint: string;
}

// Response utility functions
export const successResponse = <T>(data?: T | null, statusMessage?: string | null): Response<T> => {
    return {
        statusMessage,
        data,
        statusCode: SUCCESS_STATUS_CODE,
    };
};

const ERROR_MESSAGE = {
    NOT_LINKABLE: "{0} is not a linkable pay later method",
    NOT_SUPPORTED: "{0} does not support {1} action.",
};

export function getPayLaterHttpOptions(
    payLaterCode: LinkablePayLater,
    key: keyof LinkPayLaterURIValue,
    method: Partial<HTTP_METHODS> = HTTP_METHODS.GET,
): Result<PayLaterHttpOptionsResponse, string> {
    const payLaterMethod = LINK_PAYLATER_URI[payLaterCode];
    if (isNil(payLaterMethod)) {
        return Err(format(ERROR_MESSAGE.NOT_LINKABLE, payLaterCode));
    }

    const value = payLaterMethod[key];
    if (isNil(value)) {
        return Err(format(ERROR_MESSAGE.NOT_SUPPORTED, payLaterCode, key));
    }

    if (isString(value)) {
        return Ok({ endpoint: value, method });
    }

    return Ok(value);
}

export function transformCheckPayLaterApiResponse(response: Response<PayLaterResponse>): Response<GetPayLaterResponse> {
    if (!isObject(response) || isNil(response.statusCode) || response.statusCode !== 0) {
        return successResponse({ linked: false });
    }
    return successResponse({
        linked: response.data?.linked ?? true,
        enabled: response.data?.enabled,
        disableMessage: response.data?.disableMessage,
    });
}

const TECHNICAL_FAILURE_STATUS_CODE = 1;

export function transformCreditLimitApiResponse(
    response: Response,
): PayLaterCreditLimitData | CreditLimitBusinessError | null {
    if (!isObject(response) || isNil(response.statusCode) || typeof response.statusCode !== "number") {
        return null;
    }
    if (response.statusCode === SUCCESS_STATUS_CODE) {
        return (response.data as PayLaterCreditLimitData) ?? null;
    }
    if (response.statusCode === TECHNICAL_FAILURE_STATUS_CODE) {
        return null;
    }
    return {
        isBusinessError: true,
        statusMessage: response.statusMessage || "",
    };
}
